import React, { useEffect, useState } from 'react';
import { Palette, Loader2, Save, RefreshCw, Hash, Type, MessageSquare, Image as ImageIcon, CheckCircle, Upload, Trash2, Eye, EyeOff } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { adminFetch } from '../lib/api';

export const MarketingBrandingPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Settings
  const [brandColors, setBrandColors] = useState({ primary: '#E03705', secondary: '#FF6B35', bg: '#1A1A2E' });
  const [toneOfVoice, setToneOfVoice] = useState('Freundlich, motivierend, jung, informativ. Du-Form. Emojis sparsam einsetzen.');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [defaultHashtags, setDefaultHashtags] = useState<string[]>([]);
  const [hashtagInput, setHashtagInput] = useState('');
  const [logoOverlay, setLogoOverlay] = useState({ enabled: true, position: 'bottom-right', size: 60 });
  const [logoUrl, setLogoUrl] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [customImageRules, setCustomImageRules] = useState('');
  const [showBaseRules, setShowBaseRules] = useState(false);

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const resp = await adminFetch(`/api/admin/marketing/settings`, {
              });
      const data = await resp.json();

      if (data.brand_colors) {
        const bc = typeof data.brand_colors === 'string' ? JSON.parse(data.brand_colors) : data.brand_colors;
        setBrandColors(bc);
      }
      if (data.tone_of_voice) {
        const tv = typeof data.tone_of_voice === 'string' ? data.tone_of_voice.replace(/^"|"$/g, '') : data.tone_of_voice;
        setToneOfVoice(tv);
      }
      if (data.system_prompt) {
        const sp = typeof data.system_prompt === 'string' ? data.system_prompt.replace(/^"|"$/g, '') : data.system_prompt;
        setSystemPrompt(sp);
      }
      if (data.default_hashtags) {
        const dh = Array.isArray(data.default_hashtags) ? data.default_hashtags : (typeof data.default_hashtags === 'string' ? JSON.parse(data.default_hashtags) : []);
        setDefaultHashtags(dh);
      }
      if (data.logo_overlay) {
        const lo = typeof data.logo_overlay === 'string' ? JSON.parse(data.logo_overlay) : data.logo_overlay;
        setLogoOverlay(lo);
      }
      if (data.marketing_logo_url) {
        const lu = typeof data.marketing_logo_url === 'string' ? data.marketing_logo_url.replace(/^"|"$/g, '') : data.marketing_logo_url;
        setLogoUrl(lu);
      }
      if (data.custom_image_rules) {
        const cir = typeof data.custom_image_rules === 'string' ? data.custom_image_rules.replace(/^"|"$/g, '') : data.custom_image_rules;
        setCustomImageRules(cir);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminFetch(`/api/admin/marketing/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          brand_colors: brandColors,
          tone_of_voice: toneOfVoice,
          system_prompt: systemPrompt,
          default_hashtags: defaultHashtags,
          logo_overlay: logoOverlay,
          marketing_logo_url: logoUrl,
          custom_image_rules: customImageRules,
        }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error(err);
      alert('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const addHashtag = () => {
    const tag = hashtagInput.trim().startsWith('#') ? hashtagInput.trim() : `#${hashtagInput.trim()}`;
    if (tag.length > 1 && !defaultHashtags.includes(tag)) {
      setDefaultHashtags(prev => [...prev, tag]);
      setHashtagInput('');
    }
  };

  const removeHashtag = (tag: string) => {
    setDefaultHashtags(prev => prev.filter(t => t !== tag));
  };

  const handleLogoUpload = async (file: File) => {
    setUploading(true);
    try {
      const ext = file.name.split('.').pop() || 'png';
      const filePath = `marketing_logo_${Date.now()}.${ext}`;
      const { error: uploadErr } = await supabase.storage.from('logos').upload(filePath, file, {
        contentType: file.type,
        upsert: true,
      });
      if (uploadErr) {
        // If bucket doesn't exist, use question-images bucket
        const fallbackPath = `marketing/logo_${Date.now()}.${ext}`;
        const { error: fallbackErr } = await supabase.storage.from('question-images').upload(fallbackPath, file, {
          contentType: file.type,
          upsert: true,
        });
        if (fallbackErr) throw fallbackErr;
        const { data: urlData } = supabase.storage.from('question-images').getPublicUrl(fallbackPath);
        setLogoUrl(urlData.publicUrl);
      } else {
        const { data: urlData } = supabase.storage.from('logos').getPublicUrl(filePath);
        setLogoUrl(urlData.publicUrl);
      }
    } catch (err: any) {
      console.error(err);
      alert(`Upload fehlgeschlagen: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div>
        <div className="page-header"><h1><Palette size={24} /> CI & Branding</h1></div>
        <div style={{ textAlign: 'center', padding: 60 }}><Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} /></div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1><Palette size={24} /> CI & Branding</h1>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : saved ? <CheckCircle size={14} /> : <Save size={14} />}
          {saved ? 'Gespeichert!' : 'Speichern'}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' }}>

        {/* Left column */}
        <div>
          {/* Brand Colors */}
          <div className="card mb-4" style={{ padding: 20 }}>
            <h3 style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Palette size={18} /> Markenfarben
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div>
                <label className="text-xs text-muted" style={{ display: 'block', marginBottom: 4 }}>Primärfarbe</label>
                <div className="flex gap-2 items-center">
                  <input type="color" value={brandColors.primary} onChange={e => setBrandColors(c => ({ ...c, primary: e.target.value }))} style={{ width: 36, height: 36, border: 'none', cursor: 'pointer', borderRadius: 6 }} />
                  <input className="input" value={brandColors.primary} onChange={e => setBrandColors(c => ({ ...c, primary: e.target.value }))} style={{ fontFamily: 'monospace', fontSize: 12 }} />
                </div>
              </div>
              <div>
                <label className="text-xs text-muted" style={{ display: 'block', marginBottom: 4 }}>Sekundärfarbe</label>
                <div className="flex gap-2 items-center">
                  <input type="color" value={brandColors.secondary} onChange={e => setBrandColors(c => ({ ...c, secondary: e.target.value }))} style={{ width: 36, height: 36, border: 'none', cursor: 'pointer', borderRadius: 6 }} />
                  <input className="input" value={brandColors.secondary} onChange={e => setBrandColors(c => ({ ...c, secondary: e.target.value }))} style={{ fontFamily: 'monospace', fontSize: 12 }} />
                </div>
              </div>
              <div>
                <label className="text-xs text-muted" style={{ display: 'block', marginBottom: 4 }}>Hintergrund</label>
                <div className="flex gap-2 items-center">
                  <input type="color" value={brandColors.bg} onChange={e => setBrandColors(c => ({ ...c, bg: e.target.value }))} style={{ width: 36, height: 36, border: 'none', cursor: 'pointer', borderRadius: 6 }} />
                  <input className="input" value={brandColors.bg} onChange={e => setBrandColors(c => ({ ...c, bg: e.target.value }))} style={{ fontFamily: 'monospace', fontSize: 12 }} />
                </div>
              </div>
            </div>
            {/* Color preview */}
            <div className="mt-3 flex gap-2">
              {Object.entries(brandColors).map(([key, color]) => (
                <div key={key} style={{ width: 80, height: 40, background: color, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 10, fontWeight: 600, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                  {key}
                </div>
              ))}
            </div>
          </div>

          {/* Tone of Voice */}
          <div className="card mb-4" style={{ padding: 20 }}>
            <h3 style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Type size={18} /> Tone of Voice
            </h3>
            <textarea
              className="input"
              value={toneOfVoice}
              onChange={e => setToneOfVoice(e.target.value)}
              rows={3}
              style={{ resize: 'vertical', fontFamily: 'inherit', width: '100%' }}
              placeholder="Beschreibe den Ton der Marke..."
            />
            <p className="text-xs text-muted mt-1">Wird bei jeder Post-Generierung als Anweisung mitgegeben.</p>
          </div>

          {/* Logo Upload & Overlay */}
          <div className="card" style={{ padding: 20 }}>
            <h3 style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
              <ImageIcon size={18} /> Marketing-Logo
            </h3>

            {/* Logo preview / upload */}
            <div className="flex gap-4 items-start mb-4">
              {logoUrl ? (
                <div style={{ position: 'relative' }}>
                  <img
                    src={logoUrl}
                    alt="Marketing Logo"
                    style={{ width: 100, height: 100, objectFit: 'contain', borderRadius: 10, border: '1px solid var(--border)', background: '#f9f9f9', padding: 8 }}
                  />
                  <button
                    onClick={() => setLogoUrl('')}
                    style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', background: 'var(--danger)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    title="Logo entfernen"
                  >✕</button>
                </div>
              ) : (
                <div style={{ width: 100, height: 100, borderRadius: 10, border: '2px dashed var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
                  <ImageIcon size={32} />
                </div>
              )}
              <div style={{ flex: 1 }}>
                <p className="text-sm mb-2">Lade ein separates Logo für Instagram-Posts hoch. Empfohlen: PNG mit transparentem Hintergrund, weiße oder helle Version.</p>
                <label className={`btn ${uploading ? 'btn-secondary' : 'btn-primary'}`} style={{ cursor: uploading ? 'wait' : 'pointer', display: 'inline-flex' }}>
                  {uploading ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Upload size={14} />}
                  {uploading ? 'Hochladen...' : logoUrl ? 'Logo ersetzen' : 'Logo hochladen'}
                  <input
                    type="file"
                    accept="image/png,image/svg+xml,image/jpeg,image/webp"
                    style={{ display: 'none' }}
                    disabled={uploading}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleLogoUpload(file);
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
            </div>

            {/* Overlay settings */}
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, marginTop: 4 }}>
              <div className="flex gap-4 items-center mb-3">
                <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input type="checkbox" checked={logoOverlay.enabled} onChange={e => setLogoOverlay(lo => ({ ...lo, enabled: e.target.checked }))} />
                  Logo auf generierte Bilder einblenden
                </label>
              </div>
              {logoOverlay.enabled && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label className="text-xs text-muted" style={{ display: 'block', marginBottom: 4 }}>Position</label>
                    <select className="input" value={logoOverlay.position} onChange={e => setLogoOverlay(lo => ({ ...lo, position: e.target.value }))}>
                      <option value="bottom-right">Unten rechts</option>
                      <option value="bottom-left">Unten links</option>
                      <option value="top-right">Oben rechts</option>
                      <option value="top-left">Oben links</option>
                      <option value="center">Mitte</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-muted" style={{ display: 'block', marginBottom: 4 }}>Größe (px)</label>
                    <input className="input" type="number" value={logoOverlay.size} onChange={e => setLogoOverlay(lo => ({ ...lo, size: parseInt(e.target.value) || 60 }))} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right column */}
        <div>
          {/* System Prompt */}
          <div className="card mb-4" style={{ padding: 20 }}>
            <h3 style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
              <MessageSquare size={18} /> KI System-Prompt
            </h3>
            <textarea
              className="input"
              value={systemPrompt}
              onChange={e => setSystemPrompt(e.target.value)}
              rows={8}
              style={{ resize: 'vertical', fontFamily: 'inherit', width: '100%', fontSize: 13 }}
              placeholder="Definiere die Rolle und Persönlichkeit der KI für die Post-Generierung..."
            />
            <p className="text-xs text-muted mt-1">Dieser Prompt wird als System-Kontext bei jeder Post-Generierung verwendet. Er definiert die „Persönlichkeit" der KI.</p>
          </div>

          {/* Default Hashtags */}
          <div className="card" style={{ padding: 20 }}>
            <h3 style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Hash size={18} /> Standard-Hashtags
            </h3>
            <div className="flex gap-2 mb-3" style={{ flexWrap: 'wrap' }}>
              {defaultHashtags.map(tag => (
                <span key={tag} className="badge badge-info" style={{ cursor: 'pointer', fontSize: 12, padding: '4px 10px' }} onClick={() => removeHashtag(tag)}>
                  {tag} ✕
                </span>
              ))}
              {defaultHashtags.length === 0 && <span className="text-xs text-muted">Keine Hashtags definiert</span>}
            </div>
            <div className="flex gap-2">
              <input
                className="input"
                value={hashtagInput}
                onChange={e => setHashtagInput(e.target.value)}
                placeholder="#neuer_hashtag"
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addHashtag(); } }}
                style={{ flex: 1 }}
              />
              <button className="btn btn-secondary" onClick={addHashtag} disabled={!hashtagInput.trim()}>
                Hinzufügen
              </button>
            </div>
            <p className="text-xs text-muted mt-2">Diese Hashtags werden automatisch bei jeder Post-Generierung vorgeschlagen. Klicke auf einen Hashtag um ihn zu entfernen.</p>
          </div>

          {/* Preview card */}
          <div className="card mt-4" style={{ padding: 16, background: `linear-gradient(135deg, ${brandColors.primary}15, ${brandColors.secondary}15)`, borderLeft: `3px solid ${brandColors.primary}` }}>
            <p className="text-sm" style={{ fontWeight: 600, marginBottom: 4 }}>Vorschau: Markenidentität</p>
            <div className="flex gap-2 items-center">
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: `linear-gradient(135deg, ${brandColors.primary}, ${brandColors.secondary})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 14 }}>F</div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, color: brandColors.primary }}>fahryo.de</div>
                <div className="text-xs text-muted">{toneOfVoice.substring(0, 50)}...</div>
              </div>
            </div>
            <div className="mt-2 text-xs" style={{ color: brandColors.primary }}>{defaultHashtags.slice(0, 5).join(' ')}</div>
          </div>
        </div>
      </div>

      {/* ─── BRAND ASSETS GALLERY ───────────────────────────────────── */}
      <BrandAssetsGallery />

      {/* ─── CUSTOM IMAGE RULES ─────────────────────────────────── */}
      <div className="card" style={{ marginTop: 24, padding: 20 }}>
        <h3 style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
          <ImageIcon size={20} /> Bildgenerierungs-Regeln
        </h3>
        <p className="text-xs text-muted" style={{ marginBottom: 16, lineHeight: 1.5 }}>
          Diese Regeln werden bei <strong>jeder</strong> KI-Bildgenerierung angehängt – Marketing-Posts, Fragen-Bilder, Image Review etc.
          Die Basis-Regeln (Handy-Orientierung, Anatomie, Text-frei) sind fest eingebaut. Hier kannst du eigene Regeln hinzufügen.
        </p>

        {/* Show/hide base rules */}
        <button
          className="btn btn-ghost btn-sm flex items-center gap-1"
          onClick={() => setShowBaseRules(!showBaseRules)}
          style={{ marginBottom: 12, fontSize: 12 }}
        >
          {showBaseRules ? <EyeOff size={12} /> : <Eye size={12} />}
          {showBaseRules ? 'Basis-Regeln ausblenden' : 'Basis-Regeln anzeigen (nur lesen)'}
        </button>

        {showBaseRules && (
          <div style={{
            background: 'rgba(139,92,246,0.06)', borderRadius: 10,
            padding: 16, marginBottom: 16,
            border: '1px solid rgba(139,92,246,0.15)',
            fontFamily: 'monospace', fontSize: 11, lineHeight: 1.7,
            color: 'var(--text-muted)', whiteSpace: 'pre-wrap', maxHeight: 300, overflow: 'auto',
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#8B5CF6', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>🔒 Fest eingebaute Basis-Regeln</div>
{`1. PHONE/DISPLAY ORIENTATION: Screen faces the person, back faces camera.
2. HAND ANATOMY: Exactly 5 fingers, natural bending.
3. TEXT IN IMAGES: No text/watermarks unless requested.
4. PHYSICAL REALISM: Correct reflections, shadows, gravity.
5. FACE CONSISTENCY: Anatomically correct faces.`}
          </div>
        )}

        {/* Editable custom rules */}
        <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6, color: 'var(--text)' }}>
          ✏️ Eigene Zusatzregeln
        </label>
        <textarea
          className="input"
          value={customImageRules}
          onChange={e => setCustomImageRules(e.target.value)}
          rows={6}
          style={{
            resize: 'vertical', fontFamily: 'monospace', fontSize: 12, width: '100%',
            lineHeight: 1.6, background: 'var(--bg-secondary)',
          }}
          placeholder={`Beispiele:
6. Niemals Wasserflaschen im Bild zeigen.
7. Personen sollen immer lächeln.
8. Hintergrund soll immer urban/städtisch sein.
9. Farben sollen warm und einladend wirken.`}
        />
        <p className="text-xs text-muted mt-1">
          Nummeriere deine Regeln ab 6 (Basis-Regeln sind 1-5). Schreibe auf Englisch für beste Ergebnisse. Änderungen werden mit "Speichern" oben rechts übernommen.
        </p>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

// ─── Brand Assets Gallery Component ──────────────────────────────

interface Asset { id: string; type: string; label: string; description: string | null; url: string; created_at: string; }

const ASSET_TYPES = [
  { id: 'screenshot', label: '📱 Screenshot', color: '#3B82F6' },
  { id: 'logo', label: '🎨 Logo', color: '#E03705' },
  { id: 'style_reference', label: '✨ Style-Referenz', color: '#8B5CF6' },
  { id: 'other', label: '📁 Sonstiges', color: '#6B7280' },
];

const BrandAssetsGallery: React.FC = () => {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [assetsLoading, setAssetsLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [filterType, setFilterType] = useState<string>('all');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string>('');

  // Upload form
  const [uploadLabel, setUploadLabel] = useState('');
  const [uploadType, setUploadType] = useState('screenshot');
  const [uploadDesc, setUploadDesc] = useState('');
  const [uploadPreview, setUploadPreview] = useState('');
  const [uploadData, setUploadData] = useState('');
  const [uploadMime, setUploadMime] = useState('');

  useEffect(() => { loadAssets(); }, []);

  const loadAssets = async () => {
    setAssetsLoading(true);
    try {
      const resp = await adminFetch(`/api/admin/marketing/assets`, {
              });
      const data = await resp.json();
      setAssets(data.assets || []);
    } catch (e) { console.error(e); }
    finally { setAssetsLoading(false); }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      setUploadPreview(result);
      setUploadData(result.split(',')[1]);
      setUploadMime(file.type);
      if (!uploadLabel) setUploadLabel(file.name.replace(/\.[^.]+$/, ''));
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!uploadData || !uploadLabel) return;
    setUploading(true);
    try {
      const resp = await adminFetch(`/api/admin/marketing/assets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageData: uploadData, mimeType: uploadMime, label: uploadLabel, type: uploadType, description: uploadDesc }),
      });
      if (resp.ok) {
        setUploadOpen(false);
        setUploadLabel(''); setUploadDesc(''); setUploadPreview(''); setUploadData('');
        await loadAssets();
      } else {
        const d = await resp.json();
        alert(d.error || 'Upload fehlgeschlagen');
      }
    } catch (e: any) { alert(e.message); }
    finally { setUploading(false); }
  };

  const handleDelete = async (id: string) => {
    try {
      await adminFetch(`/api/admin/marketing/assets/${id}`, {
        method: 'DELETE',       });
      setDeleteConfirmId('');
      await loadAssets();
    } catch (e: any) { alert(e.message); }
  };

  const handleChangeType = async (id: string, newType: string) => {
    try {
      await adminFetch(`/api/admin/marketing/assets/${id}/type`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: newType }),
      });
      setAssets(prev => prev.map(a => a.id === id ? { ...a, type: newType } : a));
    } catch (e: any) { console.error(e); }
  };

  const filtered = filterType === 'all' ? assets : assets.filter(a => a.type === filterType);

  return (
    <div className="card" style={{ marginTop: 24, padding: 20 }}>
      <div className="flex justify-between items-center" style={{ marginBottom: 16 }}>
        <h3 className="text-lg font-bold flex items-center gap-2"><ImageIcon size={20} /> Brand Assets</h3>
        <button className="btn btn-primary btn-sm flex items-center gap-1" onClick={() => setUploadOpen(!uploadOpen)}>
          <Upload size={14} /> Asset hochladen
        </button>
      </div>
      <p className="text-xs text-muted" style={{ marginBottom: 16 }}>Lade Screenshots, Logos und Style-Referenzen hoch, die die KI als Vorlage für Bildgenerierung nutzen kann.</p>

      {/* Upload Form */}
      {uploadOpen && (
        <div className="card" style={{ padding: 20, background: 'var(--bg-secondary)', marginBottom: 20 }}>
          <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Neues Asset hochladen</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label className="text-xs font-bold text-muted" style={{ display: 'block', marginBottom: 4 }}>Bild auswählen *</label>
              <input type="file" accept="image/*" onChange={handleFileSelect}
                style={{ fontSize: 12, padding: 8, width: '100%', background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 8 }} />
              {uploadPreview && (
                <img src={uploadPreview} alt="Preview" style={{ width: 160, height: 120, objectFit: 'cover', borderRadius: 8, marginTop: 8, border: '1px solid var(--border)' }} />
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label className="text-xs font-bold text-muted" style={{ display: 'block', marginBottom: 4 }}>Name *</label>
                <input value={uploadLabel} onChange={e => setUploadLabel(e.target.value)} placeholder="z.B. Dashboard Screenshot"
                  className="input-field" style={{ fontSize: 13, padding: '8px 12px', width: '100%' }} />
              </div>
              <div>
                <label className="text-xs font-bold text-muted" style={{ display: 'block', marginBottom: 4 }}>Kategorie</label>
                <select value={uploadType} onChange={e => setUploadType(e.target.value)}
                  style={{ fontSize: 13, padding: '8px 12px', width: '100%', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}>
                  {ASSET_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-muted" style={{ display: 'block', marginBottom: 4 }}>Beschreibung (optional)</label>
              <input value={uploadDesc} onChange={e => setUploadDesc(e.target.value)} placeholder="Was zeigt das Bild?"
                className="input-field" style={{ fontSize: 13, padding: '8px 12px', width: '100%' }} />
            </div>
          </div>
          <div className="flex gap-3" style={{ marginTop: 16, justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost" onClick={() => setUploadOpen(false)} style={{ padding: '8px 16px', fontSize: 13 }}>Abbrechen</button>
            <button className="btn btn-primary flex items-center gap-1" onClick={handleUpload} disabled={uploading || !uploadData || !uploadLabel} style={{ padding: '8px 20px', fontSize: 13 }}>
              {uploading ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Lädt...</> : 'Hochladen'}
            </button>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-2 flex-wrap" style={{ marginBottom: 16 }}>
        <button onClick={() => setFilterType('all')}
          className={`btn btn-sm ${filterType === 'all' ? 'btn-primary' : 'btn-ghost'}`} style={{ fontSize: 12, padding: '6px 12px' }}>
          Alle ({assets.length})
        </button>
        {ASSET_TYPES.map(t => {
          const count = assets.filter(a => a.type === t.id).length;
          return (
            <button key={t.id} onClick={() => setFilterType(t.id)}
              className={`btn btn-sm ${filterType === t.id ? 'btn-primary' : 'btn-ghost'}`} style={{ fontSize: 12, padding: '6px 12px' }}>
              {t.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Grid */}
      {assetsLoading ? (
        <div className="flex justify-center" style={{ padding: '40px 0' }}><Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: '#E03705' }} /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-muted text-sm" style={{ padding: '40px 0' }}>
          <ImageIcon size={40} style={{ opacity: 0.3, margin: '0 auto 12px', display: 'block' }} />
          <p style={{ fontWeight: 600 }}>Keine Assets vorhanden</p>
          <p className="text-xs" style={{ marginTop: 4 }}>Lade Screenshots und Logos hoch, damit die KI sie als Referenz nutzen kann.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
          {filtered.map(asset => {
            const typeInfo = ASSET_TYPES.find(t => t.id === asset.type);
            return (
              <div key={asset.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <img src={asset.url} alt={asset.label} style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover' }} />
                <div style={{ padding: '12px 14px' }}>
                  <div className="text-sm font-bold" style={{ lineHeight: 1.4, marginBottom: 4 }}>{asset.label}</div>
                  {asset.description && <div className="text-xs text-muted" style={{ lineHeight: 1.4, marginBottom: 8 }}>{asset.description}</div>}

                  {/* Type selector */}
                  <select
                    value={asset.type}
                    onChange={e => handleChangeType(asset.id, e.target.value)}
                    style={{
                      fontSize: 11, padding: '4px 8px', width: '100%', borderRadius: 6,
                      border: '1px solid var(--border)', background: 'var(--bg-secondary)',
                      color: typeInfo?.color, fontWeight: 600, marginBottom: 8, cursor: 'pointer',
                    }}
                  >
                    {ASSET_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>

                  {/* Actions */}
                  <button
                    onClick={() => setDeleteConfirmId(asset.id)}
                    className="btn btn-ghost btn-sm"
                    style={{ width: '100%', fontSize: 11, color: '#EF4444', padding: '5px 0' }}
                  >
                    <Trash2 size={12} /> Löschen
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.7)' }} onClick={() => setDeleteConfirmId('')}>
          <div style={{ background: '#1a1a2e', borderRadius: 16, padding: 28, maxWidth: 360, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.1)' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 8, color: '#fff' }}>🗑️ Asset löschen?</h3>
            <p style={{ fontSize: 14, marginBottom: 20, color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>Dieses Asset wird aus der Bibliothek entfernt.</p>
            <div className="flex gap-3 justify-end">
              <button className="btn btn-ghost" onClick={() => setDeleteConfirmId('')} style={{ padding: '8px 16px', fontSize: 13 }}>Abbrechen</button>
              <button className="btn btn-danger" onClick={() => handleDelete(deleteConfirmId)} style={{ padding: '8px 20px', fontSize: 13 }}>Löschen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
